import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
type Screen = 'hero' | 'decision1' | 'decision2';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/9VHVUjqgppDSdH8H4rjp3H/Ruby_Logo_new2w.svg';
const PHOTO_REAL = 'https://cdn.rawproductionsgmbh.de/ruby/rubyversum/rubyversum-5.jpg';
const PHOTO_ONLINE = 'https://cdn.rawproductionsgmbh.de/ruby/rubyversum/rubyversum-4.jpg?v=2';
const PHOTO_HERO = 'https://cdn.rawproductionsgmbh.de/ruby/rubyversum/rubyversum-9.jpg';
const PHOTO_LEVEL2 = 'https://cdn.rawproductionsgmbh.de/ruby/rubyversum/rubyversum-14.jpg?v=2';
const PHOTO_PLATFORM = 'https://cdn.rawproductionsgmbh.de/ruby/rubyversum/rubyversum-3.jpg';
const PHOTO_ERZIEHUNG = 'https://cdn.rawproductionsgmbh.de/ruby/rubyversum/rubyversum-13.jpg';
const PHOTO_VIDEOSTORE = 'https://cdn.rawproductionsgmbh.de/ruby/rubyversum/rubyversum-12.jpg?v=2';
const SHIMMER_STYLES = `
@keyframes shimmer-sweep {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}
.rubyversum-shimmer {
  color: #941318;
  background: linear-gradient(
    90deg,
    #941318 0%,
    #941318 35%,
    #d1bc8a 42%,
    #ffffff 50%,
    #d1bc8a 58%,
    #941318 65%,
    #941318 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: shimmer-sweep 8s ease-in-out infinite;
}
`;
export function Rubyversum() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('hero');
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Khand:wght@400;500;600;700&family=Roboto+Condensed:wght@300;400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  const pageVariants = {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: -20
    }
  };
  const cardVariants = {
    initial: {
      opacity: 0,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      scale: 1
    },
    hover: {
      scale: 1.02
    }
  };
  return <div className="min-h-screen w-full bg-[#0A0A0A] text-white overflow-hidden" data-id="element-2">
      <style dangerouslySetInnerHTML={{
      __html: SHIMMER_STYLES
    }} data-id="element-3" />
      <AnimatePresence mode="wait" data-id="element-4">
        {currentScreen === 'hero' && <motion.div key="hero" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
        duration: 0.6,
        ease: 'easeInOut'
      }} className="min-h-screen w-full relative flex flex-col items-center justify-center px-6" data-id="element-5">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: `url(${PHOTO_HERO})`
        }} data-id="element-6">
              <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/65 via-[#0A0A0A]/50 to-[#0A0A0A]/75" data-id="element-7" />
            </div>

            {/* Ambient Red Glow */}
            <div className="absolute inset-0 pointer-events-none" data-id="element-8">
              <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#f87171]/20 rounded-full blur-[120px]" data-id="element-9" />
              <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#f87171]/15 rounded-full blur-[100px]" data-id="element-10" />
            </div>

            {/* Content */}
            <div className="relative z-10 flex flex-col items-center text-center max-w-4xl" data-id="element-11">
              <motion.img src={LOGO_URL} alt="Ruby June Logo" className="w-32 md:w-40 -mt-24 md:mt-0 mb-[180px]" initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2,
            duration: 0.6
          }} data-id="element-12" />

              <motion.h1 className="md:text-7xl lg:text-8xl font-bold tracking-[0.25em] mb-6 rubyversum-shimmer text-[38px]" style={{
            fontFamily: "'Cinzel', serif"
          }} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4,
            duration: 0.6
          }} data-id="element-13">
                RUBYVERSUM
              </motion.h1>

              <motion.p className="text-xl md:text-2xl text-[#d1bc8a] mb-12 tracking-wide" style={{
            fontFamily: "'Roboto Condensed', sans-serif"
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6,
            duration: 0.6
          }} data-id="element-14">
                Willkommen in meinem Universum
              </motion.p>

              <motion.button onClick={() => setCurrentScreen('decision1')} className="px-10 py-4 bg-transparent border-2 border-[#d1bc8a]/60 text-[#d1bc8a] text-lg font-medium tracking-widest uppercase transition-all duration-300 hover:bg-[#d1bc8a]/15 hover:text-[#d1bc8a] hover:shadow-[0_0_30px_rgba(209,188,138,0.2)]" style={{
            fontFamily: "'Roboto Condensed', sans-serif"
          }} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.8,
            duration: 0.6
          }} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.98
          }} data-id="element-15">
                Eintreten
              </motion.button>
            </div>
          </motion.div>}

        {currentScreen === 'decision1' && <motion.div key="decision1" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
        duration: 0.6,
        ease: 'easeInOut'
      }} className="min-h-screen w-full relative flex flex-col items-center justify-center px-6 py-12" data-id="element-16">
            {/* Background Image with Overlay - hidden on mobile */}
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat hidden md:block" style={{
          backgroundImage: `url(${PHOTO_LEVEL2})`
        }} data-id="element-17">
              <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/75 via-[#0A0A0A]/65 to-[#0A0A0A]/80" data-id="element-18" />
            </div>

            {/* Ambient Glow */}
            <div className="absolute inset-0 pointer-events-none" data-id="element-19">
              <div className="absolute top-1/3 left-0 w-72 h-72 bg-[#f87171]/20 rounded-full blur-[100px]" data-id="element-20" />
              <div className="absolute bottom-1/3 right-0 w-72 h-72 bg-[#fde047]/10 rounded-full blur-[100px]" data-id="element-21" />
            </div>

            <div className="relative z-10 w-full max-w-5xl" data-id="element-22">
              <motion.h2 className="text-3xl md:text-4xl lg:text-5xl text-center mb-4 text-[#d90000]" style={{
            fontFamily: "'Khand', sans-serif"
          }} initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-23">
                Wie möchtest du Ruby June erleben?
              </motion.h2>

              <motion.p className="text-[#fde047]/70 text-center mb-12 text-lg" style={{
            fontFamily: "'Roboto Condensed', sans-serif"
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.3
          }} data-id="element-24">
                Triff deine Wahl
              </motion.p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-25">
                {/* Real Card */}
                <motion.a href="https://ruby-june.de/" target="_blank" rel="noopener noreferrer" className="group relative h-[400px] md:h-[500px] rounded-lg overflow-hidden cursor-pointer" variants={cardVariants} initial="initial" animate="animate" whileHover="hover" transition={{
              delay: 0.4,
              duration: 0.5
            }} data-id="element-26">
                  <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{
                backgroundImage: `url(${PHOTO_REAL})`
              }} data-id="element-27" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" data-id="element-28" />
                  <div className="absolute inset-0 border-2 border-[#f87171]/50 rounded-lg group-hover:border-[#f87171] group-hover:shadow-[0_0_40px_rgba(248,113,113,0.3)] transition-all duration-300" data-id="element-29" />

                  <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-30">
                    <h3 className="text-3xl md:text-4xl font-bold text-[#d90000] mb-3 transition-transform duration-300 origin-left group-hover:scale-110" style={{
                  fontFamily: "'Khand', sans-serif"
                }} data-id="element-31">
                      Real erleben
                    </h3>
                    <p className="text-gray-300 text-lg" style={{
                  fontFamily: "'Roboto Condensed', sans-serif"
                }} data-id="element-32">
                      Persönliche Session in meinem Studio
                    </p>
                    <div className="mt-4 inline-flex items-center text-[#d1bc8a] font-medium group-hover:text-[#d1bc8a]/80 transition-colors" data-id="element-33">
                      <span style={{
                    fontFamily: "'Roboto Condensed', sans-serif"
                  }} data-id="element-34">
                        Zur Website
                      </span>
                      <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-35">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-36" />
                      </svg>
                    </div>
                  </div>
                </motion.a>

                {/* Online Card */}
                <motion.button onClick={() => setCurrentScreen('decision2')} className="group relative h-[400px] md:h-[500px] rounded-lg overflow-hidden cursor-pointer text-left" variants={cardVariants} initial="initial" animate="animate" whileHover="hover" transition={{
              delay: 0.5,
              duration: 0.5
            }} data-id="element-37">
                  <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{
                backgroundImage: `url(${PHOTO_ONLINE})`
              }} data-id="element-38" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" data-id="element-39" />
                  <div className="absolute inset-0 border-2 border-[#fde047]/40 rounded-lg group-hover:border-[#fde047]/70 group-hover:shadow-[0_0_40px_rgba(253,224,71,0.25)] transition-all duration-300" data-id="element-40" />

                  <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-41">
                    <h3 className="text-3xl md:text-4xl font-bold text-[#d90000] mb-3 transition-transform duration-300 origin-left group-hover:scale-110" style={{
                  fontFamily: "'Khand', sans-serif"
                }} data-id="element-42">
                      Online erleben
                    </h3>
                    <p className="text-gray-300 text-lg" style={{
                  fontFamily: "'Roboto Condensed', sans-serif"
                }} data-id="element-43">
                      Erziehung, Austausch & exklusive Einblicke
                    </p>
                    <div className="mt-4 inline-flex items-center text-[#d1bc8a] font-medium group-hover:text-[#d1bc8a]/80 transition-colors" data-id="element-44">
                      <span style={{
                    fontFamily: "'Roboto Condensed', sans-serif"
                  }} data-id="element-45">
                        Online entdecken
                      </span>
                      <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-46">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-47" />
                      </svg>
                    </div>
                  </div>
                </motion.button>
              </div>
            </div>
          </motion.div>}

        {currentScreen === 'decision2' && <motion.div key="decision2" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
        duration: 0.6,
        ease: 'easeInOut'
      }} className="min-h-screen w-full relative flex flex-col items-center justify-center px-6 py-12" data-id="element-48">
            {/* Background Image with Overlay - hidden on mobile */}
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat hidden md:block" style={{
          backgroundImage: `url(${PHOTO_PLATFORM})`
        }} data-id="element-49">
              <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/75 via-[#0A0A0A]/65 to-[#0A0A0A]/80" data-id="element-50" />
            </div>

            {/* Ambient Glow */}
            <div className="absolute inset-0 pointer-events-none" data-id="element-51">
              <div className="absolute top-1/3 left-1/4 w-72 h-72 bg-[#fde047]/15 rounded-full blur-[100px]" data-id="element-52" />
              <div className="absolute bottom-1/3 right-1/4 w-72 h-72 bg-[#f87171]/15 rounded-full blur-[100px]" data-id="element-53" />
            </div>

            <div className="relative z-10 w-full max-w-5xl" data-id="element-54">
              {/* Back Button */}
              <motion.button onClick={() => setCurrentScreen('decision1')} className="flex items-center text-[#d1bc8a]/60 hover:text-[#d1bc8a] mb-8 transition-colors" style={{
            fontFamily: "'Roboto Condensed', sans-serif"
          }} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-55">
                <ArrowLeftIcon className="w-5 h-5 mr-2" data-id="element-56" />
                Zurück
              </motion.button>

              <motion.h2 className="text-3xl md:text-4xl lg:text-5xl text-center mb-4 text-[#d90000]" style={{
            fontFamily: "'Khand', sans-serif"
          }} initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-57">
                Wähle deine Plattform
              </motion.h2>

              <motion.p className="text-[#fde047]/70 text-center mb-12 text-lg" style={{
            fontFamily: "'Roboto Condensed', sans-serif"
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4
          }} data-id="element-58">
                Wo möchtest du mich online erleben?
              </motion.p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-59">
                {/* Bestfans Card */}
                <motion.a href="https://www.bestfans.com/ruby-june" target="_blank" rel="noopener noreferrer" className="group relative h-[300px] md:h-[350px] rounded-lg overflow-hidden cursor-pointer" variants={cardVariants} initial="initial" animate="animate" whileHover="hover" transition={{
              delay: 0.5,
              duration: 0.5
            }} data-id="element-60">
                  <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{
                backgroundImage: `url(${PHOTO_ERZIEHUNG})`
              }} data-id="element-61" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/40" data-id="element-62" />
                  <div className="absolute inset-0 border-2 border-[#f87171]/50 rounded-lg group-hover:border-[#f87171] group-hover:shadow-[0_0_40px_rgba(248,113,113,0.3)] transition-all duration-300" data-id="element-63" />

                  {/* Decorative gradient */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#fde047]/15 to-transparent rounded-bl-full" data-id="element-64" />

                  <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-65">
                    <h3 className="text-3xl md:text-4xl font-bold text-[#d90000] mb-3 transition-transform duration-300 origin-left group-hover:scale-110" style={{
                  fontFamily: "'Khand', sans-serif"
                }} data-id="element-66">
                      Online Erziehung
                    </h3>
                    <p className="text-gray-300 mb-6 text-[12px]" style={{
                  fontFamily: "'Roboto Condensed', sans-serif"
                }} data-id="element-67">
                      Dein täglicher Zugang zu meiner Führung mit persönlichen
                      Nachrichten & regelmäßigen Aufgaben.
                    </p>
                    <div className="inline-flex items-center text-[#d1bc8a] font-medium group-hover:text-[#d1bc8a]/80 transition-colors" data-id="element-68">
                      <span style={{
                    fontFamily: "'Roboto Condensed', sans-serif"
                  }} data-id="element-69">
                        Bestfans besuchen
                      </span>
                      <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-70">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" data-id="element-71" />
                      </svg>
                    </div>
                  </div>
                </motion.a>

                {/* MyMemberSite Card */}
                <motion.a href="https://ruby-june-store.com/" target="_blank" rel="noopener noreferrer" className="group relative h-[300px] md:h-[350px] rounded-lg overflow-hidden cursor-pointer" variants={cardVariants} initial="initial" animate="animate" whileHover="hover" transition={{
              delay: 0.6,
              duration: 0.5
            }} data-id="element-72">
                  <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{
                backgroundImage: `url(${PHOTO_VIDEOSTORE})`
              }} data-id="element-73" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/40" data-id="element-74" />
                  <div className="absolute inset-0 border-2 border-[#f87171]/50 rounded-lg group-hover:border-[#f87171] group-hover:shadow-[0_0_40px_rgba(248,113,113,0.3)] transition-all duration-300" data-id="element-75" />

                  {/* Decorative gradient */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#f87171]/15 to-transparent rounded-bl-full" data-id="element-76" />

                  <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-77">
                    <h3 className="text-3xl md:text-4xl font-bold text-[#d90000] mb-3 transition-transform duration-300 origin-left group-hover:scale-110" style={{
                  fontFamily: "'Khand', sans-serif"
                }} data-id="element-78">
                      Mein Videostore
                    </h3>
                    <p className="text-gray-300 mb-6 text-[12px]" style={{
                  fontFamily: "'Roboto Condensed', sans-serif"
                }} data-id="element-79">
                      Alle meine BDSM-Videos für deine Fantasie & deinen Fetisch
                      auf einen Blick & sofort verfügbar.
                    </p>
                    <div className="inline-flex items-center text-[#d1bc8a] font-medium group-hover:text-[#d1bc8a]/80 transition-colors" data-id="element-80">
                      <span style={{
                    fontFamily: "'Roboto Condensed', sans-serif"
                  }} data-id="element-81">
                        Videostore besuchen
                      </span>
                      <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-82">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" data-id="element-83" />
                      </svg>
                    </div>
                  </div>
                </motion.a>
              </div>

              {/* Telegram Info Link */}
              <motion.div className="flex justify-center mt-10" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.8,
            duration: 0.5
          }} data-id="element-84">
                <a href="https://t.me/rubyjune1" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 text-[#d1bc8a]/40 hover:text-[#d1bc8a]/70 transition-colors duration-300 text-sm tracking-wide" style={{
              fontFamily: "'Roboto Condensed', sans-serif"
            }} data-id="element-85">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" data-id="element-86">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-87" />
                  </svg>
                  <span data-id="element-88">Noch unentschlossen? Folge mir auf Telegram</span>
                </a>
              </motion.div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}